/**
 * Delivery Estimate Analytics
 */

import { useEffect } from "react";

import { emitDeliveryEstimate } from "../../events";
import { DeliveryEstimate } from "../../generated/graphql";
import { DeliveryEstimateContext } from "../../types";

/**
 * Type is constructed with the intention of destructuring the `deliveryEstimate`
 * object and then providing the last few values manually.
 * @example
 * useDeliveryEstimateAnalytics({
 *   enabled: true,
 *   context: {
 *     ...deliveryEstimate,
 *     postcode: deliveryLocation?.postcode,
 *     deliveryType: selectedShippingOption?.type,
 *     parcelType: dispatchNotice?.type,
 *   },
 * });
 */
type DeliveryEstimateAnalyticsContext = Partial<
  Pick<
    DeliveryEstimate,
    | "label"
    | "labelType"
    | "shortLabel"
    | "timeStamp"
    | "minRange"
    | "maxRange"
    | "confidence"
  > &
    Pick<
      DeliveryEstimateContext["options"],
      "postcode" | "deliveryType" | "parcelType"
    >
>;

export interface DeliveryEstimateAnalyticsProps {
  /**
   * Enable delivery estimate tracking.
   * Typically, this is only enabled when the delivery estimate experiment is on.
   * @default false
   */
  enabled?: boolean;

  /**
   * Context which will be forwarded to
   * `emitDeliveryEstimate`.
   * @see emitDeliveryEstimate
   */
  context?: DeliveryEstimateAnalyticsContext;

  /**
   * Define options to change the behaviour of the hook depending on its consumer
   */
  options?: {
    fireOnFallback?: boolean;
  };
}

/**
 *
 * @example ```
 * useDeliveryEstimateAnalytics({
 *   enabled: true,
 *   context: {
 *     ...deliveryEstimate,
 *     postcode: deliveryLocation?.postcode,
 *     deliveryType: selectedShippingOption?.type,
 *     parcelType: dispatchNotice?.type,
 *   },
 * });
 * ```
 */
export function useDeliveryEstimateAnalytics({
  enabled = false,
  context,
  options,
}: DeliveryEstimateAnalyticsProps) {
  const {
    confidence,
    deliveryType,
    label,
    labelType,
    maxRange,
    minRange,
    parcelType,
    postcode,
    shortLabel,
    timeStamp,
  } = context || {};

  useEffect(() => {
    // Provide some DX by warning the developer if they've enabled analytics but
    // not provided any options.
    if (process.env.NODE_ENV === "development") {
      if (enabled && !context) {
        console.warn(
          "You've enabled delivery estimate analytics but not provided any context."
        );
      }
    }

    // Do nothing if not enabled or missing options.
    if (!enabled || !context) return;

    // Only emit if all required options are available
    if (
      !deliveryType ||
      !postcode ||
      !labelType ||
      !label ||
      !confidence ||
      !parcelType
    ) {
      return;
    }

    // Only emit for actually estimated delivery times unless fireOnFallback is enabled
    if (
      (!!options?.fireOnFallback &&
        !["Estimated", "Fallback"].includes(labelType)) ||
      (!options?.fireOnFallback && labelType !== "Estimated")
    )
      return;

    // Fix up the options to match the analytics
    const eventContext: DeliveryEstimateContext["options"] = {
      confidence: Number(confidence),
      deliveryType: deliveryType,
      label: label || "",
      labelType: labelType,
      maxRange: maxRange ?? null,
      minRange: minRange ?? null,
      parcelType: parcelType,
      postcode: postcode,
      shortLabel: shortLabel ?? null,
      timestamp: timeStamp ?? null,
    };

    // Alright, we're good to go.
    emitDeliveryEstimate(eventContext);

    // Only trigger this effect for specific context changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, deliveryType, postcode, parcelType]);
}
